import { getTriggerElementsFromElement } from '../../helpers/dom/get-trigger-elements-form-element';
import { eventNamespaceFactory } from '../../helpers/events/event-namespace-factory';

export class Dialog {
	triggers: HTMLElement[];
	initialyOpened = false;
	disableClose = false;
	rect: DOMRect | null = null;
	currentTarget: HTMLElement | null = null;
	closeElement: HTMLElement | null = null;

	private eventNamespaceFn = eventNamespaceFactory('opten.dialog');

	constructor(private element: HTMLDialogElement) {
		this.triggers = getTriggerElementsFromElement(this.element);
		this.closeElement = this.element.querySelector('.js-dialog-close');
		this.triggers.forEach(trigger =>
			trigger.addEventListener('click', e => {
				this.currentTarget = e.currentTarget as HTMLElement;
				if (this.currentTarget.hasAttribute('data-align-dialog')) {
					this.currentTarget.classList.add('is-active');
					this.rect = this.currentTarget.getBoundingClientRect();
				} else {
					this.currentTarget = null;
					this.rect = null;
				}
				this.toggle();
			})
		);
		this.closeElement?.addEventListener('click', () => {
			this.close();
		});
		this.disableClose = this.element.hasAttribute('data-disable-close') && this.element.getAttribute('data-disable-close')?.toUpperCase() == 'TRUE';

		this.element.addEventListener('click', event => {
			const rect = this.element.getBoundingClientRect();
			const isInDialog =
				rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

			if (!isInDialog && !this.disableClose) {
				this.close();
			}
		});

		this.element.addEventListener('op10.open', () => {
			if (!this.element.open) {
				this.open();
			}
		});

		this.element.addEventListener('op10.close', () => {
			if (this.element.open) {
				this.close();
			}
		});

		this.initialyOpened = this.element.hasAttribute('data-initially-opened') && this.element.getAttribute('data-initially-opened')?.toUpperCase() == 'TRUE';

		if (this.initialyOpened && this.cookieCheck() == false) {
			this.toggle();
		}
	}
	cookieCheck() {
		const cookieName = this.element.getAttribute('data-cookie-name');
		if (cookieName) {
			const cookies = document.cookie.split(';').map(cookie => cookie.trim());
			for (const cookie of cookies) {
				if (cookie.startsWith(`${cookieName}=`)) {
					return true; // cookie found
				}
			}
		}
		return false; // no cookie set
	}

	cookieSet() {
		if (this.element.hasAttribute('data-cookie-name') && this.element.hasAttribute('data-cookie-duration')) {
			const cookieName = this.element.getAttribute('data-cookie-name');
			const cookieDuration = this.element.getAttribute('data-cookie-duration');
			const date = new Date();
			date.setDate(date.getDate() + (cookieDuration as any));

			document.cookie = `${cookieName}=true; expires=${date.toUTCString()}; path=/`;
		}
	}
	toggle() {
		if (this.element.open) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		this.element.showModal();
		this.addClass('is-opening');
		this.removeClass('is-closing');
		this.removeClass('is-closed');
		this.eventNamespaceFn(this.element, 'opening');

		if (this.rect != null) {
			this.element.style.top = this.rect.bottom + 'px';
			this.element.style.left = this.rect.left + 'px';
		}

		const eventHandler = () => {
			this.removeClass('is-opening');
			this.addClass('is-open');
			this.element.removeEventListener('animationend', eventHandler, false);
			this.eventNamespaceFn(this.element, 'opened');
		};

		this.element.addEventListener('animationend', eventHandler, false);
	}

	close() {
		this.addClass('is-closing');
		this.removeClass('is-opening');
		this.removeClass('is-open');
		this.eventNamespaceFn(this.element, 'closing');

		if (this.currentTarget != null) {
			this.currentTarget.classList.remove('is-active');
		}

		const eventHandler = () => {
			this.removeClass('is-closing');
			this.element.close();
			this.addClass('is-closed');
			this.element.removeEventListener('animationend', eventHandler, false);
			this.eventNamespaceFn(this.element, 'closed');
		};

		this.element.addEventListener('animationend', eventHandler, false);
		this.cookieSet();
	}

	addClass(...tokens: string[]) {
		this.element.classList.add(...tokens);
		this.triggers.forEach(trigger => trigger.classList.add(...tokens));
	}

	removeClass(...tokens: string[]) {
		this.element.classList.remove(...tokens);
		this.triggers.forEach(trigger => trigger.classList.remove(...tokens));
	}

	getInstance() {
		return this.element;
	}
}
